import { FC } from 'react';

import { HeaderText, HeaderSize } from '@/components/ui/HeaderText';

import useLocale from '@/hooks/useLocale';

import { TypePlayGeniusOrder } from '@/lib/api/orderManager/types/TypePlayGeniusOrder';

import { formatMoney } from '@/utils/helpers';

import { TypeAccountERCTranslations } from '@/utils/translations/types/TypeAccountERCTranslations';

import { useAccordion } from '@/utils/useAccordion';

type OrderDetailsProps = {
  t: TypeAccountERCTranslations;
  orderData: TypePlayGeniusOrder;
  noAccordion?: boolean;
};

const isNumeric = (value: any) => !isNaN(value);

const OrderDetails: FC<OrderDetailsProps> = ({ t, orderData, noAccordion }) => {
  const locale = useLocale();

  const { Icon, Accordion } = useAccordion({ defaultIsOpen: true });

  const collapsedContent = (
    <div className="flex flex-row items-center justify-between my-6 md:my-8">
      <HeaderText align="left" text={t.orderDetails} size={HeaderSize.H3} />

      <Icon />
    </div>
  );

  const {
    subtotal_price,
    total_shipping,
    total_tax,
    total_price,
    currency_code,
    taxes_included,
    total_discounts,
  } = orderData || {};

  const localizedSubTotal = subtotal_price ?? 0;

  // Handle potential null or undefined total price with a fallback
  const totalPrice = total_price ?? 0;

  // Subtract total tax if taxes are included, otherwise use the total price
  const priceWithoutTax = totalPrice - (total_tax ?? 0);

  const finalPrice = taxes_included ? priceWithoutTax : totalPrice;

  const expandedContent = (
    <div className="grid gap-y-4 mb-6 md:mb-8">
      <div className="grid gap-y-0.5">
        {isNumeric(subtotal_price) && (
          <div className="flex flex-row justify-between">
            <HeaderText align="left" text={t.price} size={HeaderSize.H4} />

            <p>{formatMoney(localizedSubTotal, locale, false)}</p>
          </div>
        )}

        {isNumeric(total_shipping) && (
          <div className="flex flex-row justify-between">
            <HeaderText align="left" text={t.shipping} size={HeaderSize.H4} />

            {total_shipping ? (
              <p>{formatMoney(total_shipping, locale, false)}</p>
            ) : (
              <p className="uppercase">{t.free}</p>
            )}
          </div>
        )}

        {isNumeric(total_discounts) && (total_discounts ?? 0) > 0 && (
          <div className={`flex flex-row justify-between`}>
            <HeaderText align="left" text={t.discount} size={HeaderSize.H4} />

            <p className="w-full text-right">
              {formatMoney(-(total_discounts ?? 0), locale, false)}
            </p>
          </div>
        )}

        {isNumeric(total_tax) && !taxes_included && (
          <div className="flex flex-row justify-between">
            <HeaderText align="left" text={t.tax} size={HeaderSize.H4} />

            <p>{formatMoney(total_tax ?? 0, locale, false)}</p>
          </div>
        )}
      </div>

      <hr />

      <div>
        {isNumeric(total_price) && (
          <div className="flex flex-row justify-between">
            <HeaderText align="left" text={t.total} size={HeaderSize.H4} />

            <div className="flex flex-row gap-x-2 items-end">
              <p className="uppercase text-sm md:text-base">{currency_code}</p>

              <p className="text-lg md:text-xl font-bold">
                {formatMoney(finalPrice ?? 0, locale, false)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (noAccordion) {
    return (
      <div data-testid="no-accordion" className="flex flex-col gap-0.5">
        {expandedContent}
      </div>
    );
  }

  return (
    <Accordion
      collapsedContent={collapsedContent}
      expandedContent={expandedContent}
    />
  );
};

export default OrderDetails;
